import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { fetchRuns } from '../api/client';
import type { Run } from '../types';

interface Item { label: string; kind: string; to: string }

const PAGES: Item[] = [
  { label: 'Dashboard',   kind: 'Page', to: '/runs' },
  { label: 'Trigger run', kind: 'Page', to: '/new-run' },
  { label: 'Test cases',  kind: 'Page', to: '/test-cases' },
  { label: 'Bugs',        kind: 'Page', to: '/bugs' },
];

export function CommandPalette() {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const [sel, setSel] = useState(0);
  const [runs, setRuns] = useState<Run[]>([]);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') { e.preventDefault(); setOpen(o => !o); }
      else if (e.key === 'Escape') setOpen(false);
    };
    const onOpen = () => setOpen(true);
    window.addEventListener('keydown', onKey);
    window.addEventListener('expo:palette', onOpen);
    return () => { window.removeEventListener('keydown', onKey); window.removeEventListener('expo:palette', onOpen); };
  }, []);

  // Give focus back to whatever opened the palette.
  useEffect(() => {
    if (!open) return;
    const prev = document.activeElement as HTMLElement | null;
    return () => prev?.focus?.();
  }, [open]);

  useEffect(() => {
    if (!open) return;
    setQ(''); setSel(0);
    fetchRuns().then(setRuns).catch(() => setRuns([]));
    inputRef.current?.focus();
  }, [open]);

  const items = useMemo(() => {
    const all: Item[] = [
      ...PAGES,
      ...[...runs].sort((a, b) => b.build - a.build).map(r => ({
        label: `#${r.build} ${r.sourceDb} → ${r.destDb}`, kind: r.campaignType, to: `/runs/${r.runId}`,
      })),
    ];
    const t = q.trim().toLowerCase();
    return (t ? all.filter(i => i.label.toLowerCase().includes(t)) : all).slice(0, 30);
  }, [q, runs]);

  const go = (i?: Item) => { if (i) { navigate(i.to); setOpen(false); } };

  if (!open) return null;
  return (
    <div className="cp-back" onMouseDown={() => setOpen(false)}>
      <div className="cp" role="dialog" aria-modal="true" aria-label="Command palette" onMouseDown={e => e.stopPropagation()}>
        <input
          ref={inputRef} value={q} placeholder="Jump to a page or run…" aria-label="Search"
          onChange={e => { setQ(e.target.value); setSel(0); }}
          onKeyDown={e => {
            if (e.key === 'ArrowDown') { e.preventDefault(); setSel(s => Math.min(s + 1, items.length - 1)); }
            else if (e.key === 'ArrowUp') { e.preventDefault(); setSel(s => Math.max(s - 1, 0)); }
            else if (e.key === 'Enter') go(items[sel]);
          }}
        />
        <div className="sr-only" role="status">{items.length} result{items.length !== 1 ? 's' : ''}</div>
        {items.length === 0 ? <div className="cp-empty">No matches for “{q}”.</div> : (
          <ul role="listbox">
            {items.map((i, n) => (
              <li key={i.to} role="option" aria-selected={n === sel}
                  onMouseEnter={() => setSel(n)} onClick={() => go(i)}>
                <span>{i.label}</span><small>{i.kind}</small>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
